import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
  makeStyles,
  TextField,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CategoryIcon from '@material-ui/icons/Category';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import ImportRowDesktop from './ImportRowDesktop';
import ImportCardMobile from './ImportCardMobile';
import { POPULAR_UNIT_CODES } from '../../../constants/productUnits';

const useStyles = makeStyles((theme) => ({
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
    backgroundColor: '#17497F',
    color: '#ffffff',
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: 8,
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    overflowX: 'auto',
    paddingBottom: 4,
  },
  tableContainer: {
    maxHeight: 520,
    borderRadius: 8,
    border: '1px solid #e2e8f0',
  },
}));

const ImportStepReview = ({
  products = [],
  resolutions = {},
  selectedIndices = new Set(),
  excludedIndices = new Set(),
  onToggleSelect,
  onSelectAll,
  onRemoveItem,
  onBulkRemove,
  onBulkCategory,
  onBulkMargin,
  onBulkUnit,
  onChangeResolution,
  categoryList = [],
  onOpenFormatModal,
  onBackToUpload,
  onProceedToSummary,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [filterType, setFilterType] = useState('ALL'); // 'ALL' | 'NEW' | 'EXISTING' | 'PACKAGES'
  const [bulkCategoryAnchor, setBulkCategoryAnchor] = useState(null);
  const [bulkUnitAnchor, setBulkUnitAnchor] = useState(null);

  // Filtros aplicados
  const activeProducts = products
    .map((p, index) => ({ product: p, index }))
    .filter(({ index }) => !excludedIndices.has(index))
    .filter(({ product }) => {
      if (filterType === 'NEW') return product.analysis?.status === 'NEW';
      if (filterType === 'EXISTING') return product.analysis?.status === 'EXISTING';
      if (filterType === 'PACKAGES') return product.isPackage;
      return true;
    });

  const totalActive = products.length - excludedIndices.size;
  const isAllSelected = totalActive > 0 && selectedIndices.size === totalActive;

  return (
    <Box>
      {/* Barra de Filtros Rápidos */}
      <Box className={classes.filterBar}>
        <Chip
          label={`Todos (${totalActive})`}
          clickable
          color={filterType === 'ALL' ? 'primary' : 'default'}
          size="small"
          onClick={() => setFilterType('ALL')}
        />
        <Chip
          label="Novos Produtos"
          clickable
          color={filterType === 'NEW' ? 'primary' : 'default'}
          size="small"
          onClick={() => setFilterType('NEW')}
        />
        <Chip
          label="Existentes no Estoque"
          clickable
          color={filterType === 'EXISTING' ? 'primary' : 'default'}
          size="small"
          onClick={() => setFilterType('EXISTING')}
        />
        <Chip
          label="Embalagens Coletivas / Frações"
          clickable
          color={filterType === 'PACKAGES' ? 'primary' : 'default'}
          size="small"
          onClick={() => setFilterType('PACKAGES')}
        />
      </Box>

      {/* Barra Flutuante de Ações em Massa */}
      {selectedIndices.size > 0 && (
        <Paper className={classes.bulkBar} elevation={1}>
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            {selectedIndices.size} item(s) selecionado(s)
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              size="small"
              variant="contained"
              style={{ backgroundColor: '#ffffff', color: '#17497F', fontWeight: 600 }}
              startIcon={<CategoryIcon />}
              onClick={(e) => setBulkCategoryAnchor(e.currentTarget)}
            >
              Categoria
            </Button>
            <Button
              size="small"
              variant="contained"
              style={{ backgroundColor: '#ffffff', color: '#17497F', fontWeight: 600 }}
              onClick={(e) => setBulkUnitAnchor(e.currentTarget)}
            >
              Unidade
            </Button>
            <Button
              size="small"
              variant="contained"
              style={{ backgroundColor: '#ffffff', color: '#17497F', fontWeight: 600 }}
              startIcon={<MonetizationOnIcon />}
              onClick={() => {
                const marginPrompt = prompt('Informe a nova margem padrão (%) para os itens selecionados:', '30');
                if (marginPrompt !== null) {
                  onBulkMargin(parseFloat(marginPrompt) || 0);
                }
              }}
            >
              Margem
            </Button>
            <Button
              size="small"
              variant="contained"
              style={{ backgroundColor: '#ef4444', color: '#ffffff' }}
              startIcon={<DeleteIcon />}
              onClick={onBulkRemove}
            >
              Remover
            </Button>
          </Box>
        </Paper>
      )}

      {/* Menu de Categoria em Massa */}
      <Menu
        anchorEl={bulkCategoryAnchor}
        open={Boolean(bulkCategoryAnchor)}
        onClose={() => setBulkCategoryAnchor(null)}
      >
        {categoryList.map((cat) => (
          <MenuItem
            key={cat.id}
            onClick={() => {
              onBulkCategory(cat.id);
              setBulkCategoryAnchor(null);
            }}
          >
            {cat.name}
          </MenuItem>
        ))}
      </Menu>

      {/* Menu de Unidade em Massa */}
      <Menu
        anchorEl={bulkUnitAnchor}
        open={Boolean(bulkUnitAnchor)}
        onClose={() => setBulkUnitAnchor(null)}
      >
        {POPULAR_UNIT_CODES.map((uCode) => (
          <MenuItem
            key={uCode}
            onClick={() => {
              if (onBulkUnit) onBulkUnit(uCode);
              setBulkUnitAnchor(null);
            }}
          >
            Padronizar como <strong>{uCode}</strong>
          </MenuItem>
        ))}
      </Menu>

      {/* Renderização Condicional: Cards no Mobile / Tabela no PC */}
      {isMobile ? (
        <Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Box display="flex" alignItems="center">
              <Checkbox checked={isAllSelected} onChange={onSelectAll} size="small" />
              <Typography variant="caption" style={{ fontWeight: 600 }}>
                Selecionar Todos ({totalActive})
              </Typography>
            </Box>
          </Box>
          {activeProducts.map(({ product, index }) => (
            <ImportCardMobile
              key={index}
              product={product}
              index={index}
              resolution={resolutions[index]}
              isSelected={selectedIndices.has(index)}
              onToggleSelect={onToggleSelect}
              onRemove={onRemoveItem}
              onChangeResolution={onChangeResolution}
              categoryList={categoryList}
              onOpenFormatModal={onOpenFormatModal}
            />
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox checked={isAllSelected} onChange={onSelectAll} />
                </TableCell>
                <TableCell style={{ width: 48 }} />
                <TableCell>Produto (XML NFe)</TableCell>
                <TableCell align="center">Unid.</TableCell>
                <TableCell align="right">Qtd Total</TableCell>
                <TableCell align="right">Custo Unit.</TableCell>
                <TableCell style={{ width: 140 }}>Preço de Venda</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activeProducts.map(({ product, index }) => (
                <ImportRowDesktop
                  key={index}
                  product={product}
                  index={index}
                  resolution={resolutions[index]}
                  isSelected={selectedIndices.has(index)}
                  onToggleSelect={onToggleSelect}
                  onRemove={onRemoveItem}
                  onChangeResolution={onChangeResolution}
                  categoryList={categoryList}
                  onOpenFormatModal={onOpenFormatModal}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Ações Inferiores de Navegação */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
        <Button
          variant="outlined"
          color="default"
          startIcon={<ArrowBackIcon />}
          onClick={onBackToUpload}
        >
          Voltar para Upload
        </Button>
        <Button
          variant="contained"
          color="primary"
          endIcon={<ArrowForwardIcon />}
          onClick={onProceedToSummary}
          style={{ fontWeight: 600, padding: '8px 22px' }}
        >
          Avançar para Resumo & Confirmação
        </Button>
      </Box>
    </Box>
  );
};

export default ImportStepReview;
